import React from "react";
import { Link } from "react-router-dom";
import {
  PAGE_BREADCRUMB_ARIA_LABEL,
  PAGE_BREADCRUMB_DIVIDER,
} from "@core/constants/pageBreadcrumb";

export type PageBreadcrumbItem = {
  label: string;
  /** Parent crumbs only; omit on the current (last) item. */
  to?: string;
  /** Optional test id for the link or current crumb element. */
  testId?: string;
};

type Props = {
  items: PageBreadcrumbItem[];
  className?: string;
  "data-testid"?: string;
};

const dividerStyle = {
  ["--bs-breadcrumb-divider" as string]: `'${PAGE_BREADCRUMB_DIVIDER}'`,
} as React.CSSProperties;

/**
 * Hierarchical page trail. Renders nothing unless there is at least one
 * clickable ancestor (depth ≥ 2).
 */
const PageBreadcrumb: React.FC<Props> = ({
  items,
  className = "mb-2",
  "data-testid": dataTestId = "page-breadcrumb",
}) => {
  if (items.length < 2) {
    return null;
  }

  return (
    <nav
      aria-label={PAGE_BREADCRUMB_ARIA_LABEL}
      className={className}
      data-testid={dataTestId}
      style={dividerStyle}
    >
      <ol className="breadcrumb mb-0 small">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          if (isLast || !item.to) {
            return (
              <li
                key={`${item.label}-${index}`}
                className="breadcrumb-item active"
                aria-current="page"
                data-testid={item.testId}
              >
                {item.label}
              </li>
            );
          }
          return (
            <li key={`${item.label}-${index}`} className="breadcrumb-item">
              <Link
                to={item.to}
                className="text-primary-emphasis"
                data-testid={item.testId}
              >
                {item.label}
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default PageBreadcrumb;
